import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { Card, Button, Typography, Descriptions, Space, Spin, Alert, message } from 'antd';
import { SaveOutlined, PlusCircleOutlined } from '@ant-design/icons';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT, CTW_NODE_API_ENDPOINT } from '../utils';
import { Meta } from '../interfaces/apiMeta';
import axios from '../apiServices/monitoredAxios';

const { Title, Text, Paragraph } = Typography;

const CalculatedAudience = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const filters = queryParams.get('filters');
    const name = queryParams.get('name');
    const categories = queryParams.get('categories')?.split(',') || [];
    const [totalWeightedSize, setTotalWeightedSize] = useState<number | null>(null);
    const [totalCPMWeighted, setTotalCPMWeighted] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);


    useEffect(() => {
        setBreadcrumbs([
            {
                title: <Text style={{ cursor: 'pointer' }} onClick={() => navigate(`/audience`)}>Estimate Creation</Text>,
            },
            {
                title: <Text>Estimated Audience</Text>,
            }
        ]);
    }, [setBreadcrumbs, navigate]);

    useEffect(() => {
        const fetchAudienceMetrics = async () => {
            setLoading(true);
            setError(null);
            if (!filters) {
                setError("Filters are missing. Cannot calculate audience.");
                setLoading(false);
                return;
            }
            try {
                const result = await coreAPIService.get<{ meta: Meta, data: { estimatedSize: number, estimatedCpm: number } }>(
                    CTW_NODE_API_ENDPOINT,
                    `common/v1/get_audience_cost?filter=${encodeURIComponent(filters)}`,
                );


                if (result.meta.code === 200 && result.meta.success) {
                    setTotalWeightedSize(result.data.estimatedSize);
                    setTotalCPMWeighted(result.data.estimatedCpm);
                } else {
                    setError(result.meta.message || 'Failed to retrieve audience metrics.');
                    message.error(result.meta.message || 'Failed to retrieve audience metrics.');
                }
            } catch (err) {
                console.error('Error fetching audience metrics:', err);
                setError('An unexpected error occurred while fetching audience metrics.');
                message.error('An unexpected error occurred.');
            } finally {
                setLoading(false);
            }
        };

        fetchAudienceMetrics();
    }, [filters]);

    const handleCreateCampaign = () => {
        if (totalWeightedSize === null || totalCPMWeighted === null) {
            message.error("Audience data is not yet available to create a campaign.");
            return;
        }
        navigate('/campaign/create', {
            state: {
                audienceName: name,
                audienceFilters: filters,
                audienceCategories: categories,
                estimatedSize: totalWeightedSize,
                estimatedCpm: totalCPMWeighted
            }
        });
    };

    const handleSaveAudience = async () => {
        // Placeholder for saving audience logic
        // This would typically involve an API call
        //get filters form url parameters ?filters 
        const filters = urlParams.get('filters')
        let parsedFilters = {};
        try {
            if (filters) {
                parsedFilters = JSON.parse(filters);
            }
        } catch (e) {
            console.error("Error parsing audienceFilters:", e);
        }

        const formattedFilters = Object.keys(parsedFilters).map(key => ({
            [key]: { value: (parsedFilters as any)[key] }
        }));
        const name = urlParams.get('name')
        const categories = urlParams.get('categories')?.split(',') || []
        if (!name || !formattedFilters || totalWeightedSize === null || totalCPMWeighted === null) {
            message.warning("Audience name, filters, or metrics are missing. Cannot save.");
            return;
        }


        // console.log('Saving Audience:', { name, filters, categories, totalWeightedSize, totalCPMWeighted });

        try {
            const auth = localStorage.getItem("accessToken")
            const requestBody = {
                flag: "Draft",
                createAudienceRequest: {
                    brand_id: -1,
                    mandatoryTrueCount: formattedFilters ? formattedFilters.length - 2 : 0,
                    fields: [
                        {
                            percentage: {
                                value: 0
                            }
                        },
                        {
                            audience_group: {
                                value: name
                            }
                        },
                        ...formattedFilters
                    ],
                    estimated_cost: totalCPMWeighted,
                    estimated_size: totalWeightedSize
                },
                createVerismartCampaignData: null
            };

            const response = await axios.post(
                CTW_API_ENDPOINT + '/v2/verismart/create-Audience-and-campaign',
                requestBody,
                {
                    headers: {
                        'Authorization': "Bearer " + auth,
                        'Content-Type': 'application/json'
                    }
                }
            );
            // console.log('Saving Audience:', { name, filters, categories, totalWeightedSize, totalCPMWeighted });
            message.success(`Audience "${name}" saved (Simulated). Implement actual API call.`);
            navigate('/audience')
            // Potentially navigate or update UI state after successful save
        } catch (apiError) {
            console.error("Error saving audience:", apiError);
            message.error("An error occurred while trying to save the audience.");
        }
    };

    const handleCreateAudienceAndProceed = () => {
        // This function could first save the audience (similar to handleSaveAudience)
        // and then proceed to campaign creation or another step.
        // For now, it will act like "Create Campaign" but could be expanded.
        // console.log('Create Audience & Proceed:', { name, filters, categories, totalWeightedSize, totalCPMWeighted });
        message.info("Simulating 'Create Audience & Proceed'. This could save then navigate.");
        handleCreateCampaign(); // For now, mimics create campaign
    };

    const formatAudienceSize = (size: number | null) => {
        if (size === null) return 'N/A';
        if (size < 0.001 && size > 0) return '< 100'; // for very small non-zero numbers
        if (size === 0) return '0';
        if (size < 1) return (size * 1000).toFixed(0) + " Thousand"; // e.g. 0.5 Million -> 500 Thousand
        return size.toFixed(2) + " Million";
    };

    return (
        <div className="px-4 py-4 md:px-10">
            <Card
                title={<Title level={3} style={{ margin: 0 }}>Calculated Audience Details</Title>}
                bordered={false}
                className="shadow-lg rounded-lg"
            // extra={
            //     <Button 
            //         type="primary"
            //         icon={<RocketOutlined />}
            //         onClick={handleCreateCampaign}
            //         disabled={loading || error !== null || totalWeightedSize === null}
            //         size="large"
            //     >
            //         Create Campaign
            //     </Button>
            // }
            >
                {loading && (
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" tip="Calculating audience metrics..." />
                    </div>
                )}
                {error && !loading && (
                    <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 20 }} />
                )}
                {!loading && !error && totalWeightedSize !== null && totalCPMWeighted !== null && (
                    <>
                        <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 1, md: 1, sm: 1, xs: 1 }} layout="horizontal">
                            <Descriptions.Item labelStyle={{ fontWeight: 'bold', backgroundColor: "rgba(0, 0, 0, 0)" }} label="Audience Name">
                                <Text strong style={{ fontSize: '1.1em' }}>{name || 'N/A'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item labelStyle={{ fontWeight: 'bold', backgroundColor: "rgba(0, 0, 0, 0)" }} label="Selected Categories">
                                <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}>
                                    {categories.length > 0 ? categories.join(', ') : 'N/A'}
                                </Paragraph>
                            </Descriptions.Item>
                            <Descriptions.Item labelStyle={{ fontWeight: 'bold' }} label="Estimated Audience Size">
                                <Text style={{ fontSize: '1.1em' }}>{formatAudienceSize(totalWeightedSize)}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item labelStyle={{ fontWeight: 'bold' }} label="Estimated CPM">
                                <Text style={{ fontSize: '1.1em' }}>
                                    ₹{totalCPMWeighted !== null ? totalCPMWeighted.toFixed(2) : 'N/A'}
                                </Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <div style={{ paddingTop: 20, borderTop: '1px solid #f0f0f0', textAlign: 'center' }} className='m-auto'>
                            <Space size="middle" className='m-auto'>
                                <Button
                                    icon={<SaveOutlined />}
                                    onClick={handleSaveAudience}
                                    size="large"
                                >
                                    Save as Draft
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusCircleOutlined />}
                                    onClick={handleCreateAudienceAndProceed}
                                    size="large"
                                    ghost
                                >
                                    Proceed to create Audience
                                </Button>
                            </Space>
                        </div>
                    </>
                )}
                {!loading && !error && totalWeightedSize === null && (
                    <Alert message="No Data" description="Audience metrics could not be calculated or are not available." type="info" showIcon />
                )}
            </Card>
        </div>
    );
};

export default CalculatedAudience;
